import { c as _c } from "react/compiler-runtime";
import type { FieldError } from '@onefootprint/hooks';
import type { VaultData } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CollectCustomData } from '@/idv2/features/collect-custom-data';
import { getCustomDataDis } from '@/idv2/features/collect-custom-data/utils/get-custom-data-dis';
import { getDecryptOptions, getPatchVaultMutation } from '@/idv2/features/collect-custom-data/utils/vault-operations';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'collect-custom-data-route'
});
export const Route = createFileRoute('/onboarding/$token/collect-custom-data')({
  component: CollectCustomDataPage,
  loader: async ({
    params: {
      token
    },
    context: {
      queryClient,
      currentRequirement
    }
  }) => {
    if (currentRequirement?.kind !== 'collect_custom_data') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    const fields = getCustomDataDis(currentRequirement);
    logInfo('Collect custom data loader started', {
      requirementKind: currentRequirement.kind,
      header: currentRequirement.page.header,
      fields: fields.join(',')
    });
    const decryptedVault = await queryClient.fetchQuery(getDecryptOptions(currentRequirement.subject, {
      headers: {
        'X-Fp-Authorization': token
      },
      body: {
        fields
      }
    }));
    logInfo('Collect custom data loader completed successfully');
    return {
      currentRequirement,
      decryptedVault
    };
  }
});
export function CollectCustomDataPage() {
  const $ = _c(14);
  const {
    currentRequirement,
    decryptedVault
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const footprint = useFootprint();
  const {
    l10n,
    config
  } = Route.useRouteContext();
  const handleVaultError = useVaultMutationError();
  const disableSsnValidation = !config.isLive;
  let t0;
  if ($[0] !== currentRequirement.subject || $[1] !== token) {
    t0 = getPatchVaultMutation(currentRequirement.subject, {
      headers: {
        "X-Fp-Authorization": token
      }
    });
    $[0] = currentRequirement.subject;
    $[1] = token;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    mutateAsync: vaultMutation,
    isPending: isVaultMutationPending
  } = useMutation(t0);
  let t1;
  if ($[3] !== footprint || $[4] !== handleVaultError || $[5] !== vaultMutation) {
    t1 = async (data, onError) => {
      logInfo("Collect custom data form submission started", {
        vaultKeys: Object.keys(data).join(",")
      });
      ;
      try {
        await vaultMutation({
          body: data
        });
        logInfo("Collect custom data vault mutation succeeded");
        footprint.navigation.forward();
      } catch (t2) {
        const error = t2;
        const fieldErrors = handleVaultError(error, onError);
        const log = fieldErrors.length > 0 ? logWarn : logError;
        log("Collect custom data vault mutation failed", error, {
          fieldErrorCount: fieldErrors.length
        });
      }
    };
    $[3] = footprint;
    $[4] = handleVaultError;
    $[5] = vaultMutation;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const onSubmit = t1;
  let t2;
  if ($[7] !== currentRequirement || $[8] !== decryptedVault || $[9] !== disableSsnValidation || $[10] !== isVaultMutationPending || $[11] !== l10n || $[12] !== onSubmit) {
    t2 = <CollectCustomData l10n={l10n} isLoading={isVaultMutationPending} onSubmit={onSubmit} decryptedVault={decryptedVault} currentRequirement={currentRequirement} disableSsnValidation={disableSsnValidation} />;
    $[7] = currentRequirement;
    $[8] = decryptedVault;
    $[9] = disableSsnValidation;
    $[10] = isVaultMutationPending;
    $[11] = l10n;
    $[12] = onSubmit;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}